// #44 Ekip içi mesajlaşma — Back Office YAN PANELİ.
//
// ## Neden bu dosya var
// Sohbet ilk sürümde yalnız Android'de yapıldı. Ama kurduğumuz kanal DİKEYDİR
// (temsilci → şef → direktör) ve zincirin üst ucundaki kişiler çoğunlukla yalnız Back
// Office kullanır. Onlar muhatap listesinde GÖRÜNÜYOR, kendilerine gelen mesaj DB'ye
// YAZILIYOR, ama okuyacak/cevaplayacak hiçbir ekranları yoktu — yani saha temsilcisi
// yöneticisine yazıyor, mesaj sessizce duruyordu. Bu panel o kopuk halkayı kapatır.
//
// ## Neden sayfa değil, yan panel
// Sohbet bir "ekran işi" değil, araya giren bir iştir: kullanıcı sipariş bakarken gelen
// mesaja cevap verip kaldığı yerden devam etmeli. Ayrı bir rotaya gitmek bulunduğu işi
// terk ettirirdi. Bu yüzden panel her sayfanın üstünde açılır ve rota DEĞİŞMEZ.
//
// ## Neden polling, neden SignalR değil
// Projede gerçek-zamanlı altyapı YOK (SignalR bağımlılığı yok, Hub yok) ve web SPA hiç
// FCM token'ı kaydetmez — push yalnız mobil cihazlara gider. NotificationBell ile aynı
// yol: polling + sekme görünür olunca anında tazeleme. Açık sohbet daha sık yoklanır
// (yazışma sürerken gecikme hissedilir), kapalıyken yalnız okunmamış sayacı yoklanır.
//
// ## Yetki
// Menü hakkı YOKTUR ve olmamalıdır: uçlar self-service'tir, yetki her istekte
// organizasyon ağacından çözülür. Kullanıcı ağaçta değilse panel HİÇ GÖSTERİLMEZ —
// tetikleyici düğme bile çizilmez (kullanamayacağı bir düğme kafa karıştırır).
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { authHeaders } from '../../utils/odata';
import './TeamChatPanel.css';

// Kapalıyken yalnız sayaç; açıkken liste; sohbet açıkken mesajlar.
const POLL_IDLE_MS = 45 * 1000;
const POLL_LIST_MS = 20 * 1000;
const POLL_THREAD_MS = 10 * 1000;

// ── Genişlik ─────────────────────────────────────────────────────────────────
// Panel MODAL DEĞİLDİR: arka planı karartmaz, dışarı tıklayınca kapanmaz ve içeriği
// sola daraltır. Sebep: sohbetin asıl kullanımı "bir belgeye bakarken yazışmak" —
// modal panel tam da bunu engeller.
const WIDTH_KEY = 'datascope-teamchat-width';
const MIN_WIDTH = 320;
const DEFAULT_WIDTH = 440;
/** Ekranın yarısından fazlasını kaplamasın: panel yardımcıdır, ana iş değil. */
const maxWidth = () => Math.max(MIN_WIDTH, Math.min(760, Math.round(window.innerWidth * 0.6)));

function readStoredWidth() {
  const raw = Number(localStorage.getItem(WIDTH_KEY));
  if (!Number.isFinite(raw) || raw <= 0) return DEFAULT_WIDTH;
  return Math.min(Math.max(raw, MIN_WIDTH), maxWidth());
}

const unwrap = (p) => (p && (p.Data ?? p.data)) ?? p ?? {};

/**
 * GÖVDELİ istekler için başlıklar.
 *
 * ⚠ TUZAK: projede `authHeaders` adında İKİ ayrı yardımcı var ve farklılar —
 * `utils/odata` sürümü YALNIZ `Authorization` döner (OData GET'leri için yeterli),
 * `listPageStyles` sürümü ayrıca `Content-Type: application/json` koyar.
 * Content-Type verilmezse `fetch`, string gövdeyi `text/plain;charset=UTF-8` olarak
 * gönderir ve ASP.NET Core `[FromBody]` bunu REDDEDER (415 Unsupported Media Type) —
 * istek sunucuya ulaşır ama hiçbir zaman işlenmez.
 */
const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const RELATION_LABEL = { MANAGER: 'Yöneticim', SUBORDINATE: 'Astım', SAME_POSITION: 'Aynı pozisyon' };

/** İlişki etiketi — `depth` ayrımı burada yapılır (sunucu ayrı etiket göndermez). */
function relationLabel(relation, depth, levelName) {
  let base = RELATION_LABEL[relation] || '';
  if (relation === 'MANAGER' && depth > 1) base = 'Üst yönetim';
  if (relation === 'SUBORDINATE' && depth > 1) base = 'Alt kademe';
  return [base, levelName].filter(Boolean).join(' · ');
}

function clock(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function TeamChatPanel() {
  const [open, setOpen] = useState(false);
  const [directory, setDirectory] = useState(null);   // null = henüz bilinmiyor
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);         // { id, title, isGroup }
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const [width, setWidth] = useState(readStoredWidth);
  const [resizing, setResizing] = useState(false);

  const aliveRef = useRef(true);
  const bodyRef = useRef(null);

  useEffect(() => () => { aliveRef.current = false; }, []);

  // Panel açıkken sayfa içeriğini daralt. CSS değişkeni + body sınıfı kullanılır:
  // panelin genişliğini bilen tek yer burasıdır, layout ona uyar.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--tcp-width', `${width}px`);
    document.body.classList.toggle('tcp-open', open);
    return () => document.body.classList.remove('tcp-open');
  }, [open, width]);

  // Sürükleyerek genişletme. Pointer capture ile: fare panelin dışına çıksa da
  // sürükleme kopmaz (mouse event'leriyle window'a listener eklemeye gerek kalmaz).
  const onResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    setResizing(true);

    const onMove = (ev) => {
      // Panel SAĞDA: sola sürüklemek genişletir, bu yüzden fark ters işaretli.
      const next = Math.min(Math.max(startW + (startX - ev.clientX), MIN_WIDTH), maxWidth());
      setWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      // Yalnız bırakınca yaz: her pikselde localStorage'a yazmak gereksiz I/O olurdu.
      setWidth((w) => { localStorage.setItem(WIDTH_KEY, String(w)); return w; });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Pencere küçülünce kaydedilmiş genişlik ekranı boğabilir — üst sınıra çek.
  useEffect(() => {
    const onResize = () => setWidth((w) => Math.min(w, maxWidth()));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const authed = () => Boolean(localStorage.getItem('token'));

  // ── Rehber: kullanıcı ağaçta mı, kiminle yazışabilir ────────────────────────
  const fetchDirectory = useCallback(async () => {
    if (!authed()) { setDirectory({ isInHierarchy: false }); return; }
    try {
      const res = await fetch('/api/sales-org-hierarchy/my-contacts', { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = unwrap(await res.json());
      if (aliveRef.current) setDirectory(d);
    } catch {
      // Sessiz: rehber alınamadıysa panel gösterilmez. Hata bandı basmak, mesajlaşmayla
      // hiç ilgisi olmayan kullanıcıya anlamsız gelirdi.
      if (aliveRef.current) setDirectory({ isInHierarchy: false });
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!authed()) return;
    try {
      const res = await fetch('/api/TeamChat/conversations', { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const rows = unwrap(await res.json());
      if (aliveRef.current) setConversations(Array.isArray(rows) ? rows : []);
    } catch { /* yoksay — sonraki tur düzeltir */ }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!authed() || !conversationId) return;
    try {
      const res = await fetch(`/api/TeamChat/${conversationId}/messages?take=100`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const page = unwrap(await res.json());
      if (!aliveRef.current) return;
      setMessages(page.messages ?? page.Messages ?? []);
      // Okundu işaretle: rozet düşsün. Sessiz — başarısız olsa da sohbet okunabilir kalmalı.
      fetch(`/api/TeamChat/${conversationId}/read`, { method: 'POST', headers: authHeaders() }).catch(() => {});
    } catch { /* yoksay */ }
  }, []);

  useEffect(() => { fetchDirectory(); }, [fetchDirectory]);

  // Panel kapalıyken de sohbet listesi çekilir: okunmamış rozeti onun toplamından gelir.
  // Ayrı bir unread-count isteği atmıyoruz — liste zaten sayıları taşıyor.
  useEffect(() => {
    if (!directory?.isInHierarchy) return undefined;

    fetchConversations();
    const period = open ? POLL_LIST_MS : POLL_IDLE_MS;
    const id = setInterval(fetchConversations, period);

    // Sekme geri gelince ANINDA tazele: tarayıcı arka planda interval'i kısar,
    // kullanıcı döndüğünde bayat liste görürdü.
    const onVisible = () => { if (document.visibilityState === 'visible') fetchConversations(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [directory, open, fetchConversations]);

  // Açık sohbet daha sık yoklanır — yazışma sürerken gecikme hissedilir.
  useEffect(() => {
    if (!open || !active?.id) return undefined;
    fetchMessages(active.id);
    const id = setInterval(() => fetchMessages(active.id), POLL_THREAD_MS);
    return () => clearInterval(id);
  }, [open, active, fetchMessages]);

  // Yeni mesajda en alta kaydır.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages]);

  // Esc ile kapat.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { if (active) setActive(null); else setOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, active]);

  const openDirect = async (contact) => {
    setError(null);
    try {
      const res = await fetch(`/api/TeamChat/direct/${contact.userId}`, { method: 'POST', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Sohbet açılamadı.');
      const d = unwrap(payload);
      setActive({ id: d.conversationId ?? d.ConversationId, title: contact.fullName, isGroup: false });
      setMessages([]);
    } catch (e) { setError(e.message); }
  };

  const openGroup = async (group) => {
    setError(null);
    try {
      const res = await fetch(`/api/TeamChat/group/${group.id}`, { method: 'POST', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Ekip grubu açılamadı.');
      const d = unwrap(payload);
      setActive({ id: d.conversationId ?? d.ConversationId, title: group.name, isGroup: true });
      setMessages([]);
    } catch (e) { setError(e.message); }
  };

  const openExisting = (c) => {
    setActive({ id: c.id ?? c.Id, title: c.title ?? c.Title, isGroup: (c.kind ?? c.Kind) === 'TEAM_GROUP' });
    setMessages([]);
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || sending || !active?.id) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/TeamChat/${active.id}/messages`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ body }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Mesaj gönderilemedi.');
      const d = unwrap(payload);
      // Sunucunun DÖNDÜRDÜĞÜ mesaj eklenir: yasaklı kelime maskelenmiş olabilir. Yerel
      // eklenip sonra düzeltilse kullanıcı bir an ham metnini görür, ardından yıldızlar belirirdi.
      setMessages((prev) => [...prev, d.message ?? d.Message]);
      setDraft('');
      setNotice(d.moderationNotice ?? d.ModerationNotice ?? null);
      fetchConversations();
    } catch (e) {
      setError(e.message);   // taslak KORUNUR, kullanıcı tekrar deneyebilsin
    } finally {
      setSending(false);
    }
  };

  // Ağaçta değilse hiç çizme — kullanamayacağı bir düğme kafa karıştırır.
  if (!directory?.isInHierarchy) return null;

  const unread = conversations.reduce((sum, c) => sum + (c.unreadCount ?? c.UnreadCount ?? 0), 0);
  const badge = unread > 99 ? '99+' : String(unread);

  const groups = directory.groups ?? directory.Groups ?? [];
  const contacts = directory.contacts ?? directory.Contacts ?? [];
  // Sohbeti açılmış olan rehberde TEKRAR gösterilmez — aynı sohbeti iki yerde listelemek olurdu.
  const openTitles = new Set(conversations.map((c) => c.title ?? c.Title));
  const pendingGroups = groups.filter((g) => !openTitles.has(g.name));
  const pendingContacts = contacts.filter((c) => !openTitles.has(c.fullName));

  return (
    <>
      <button
        type="button"
        className={`tcp-trigger${unread > 0 ? ' has-unread' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={unread > 0 ? `${unread} okunmamış mesaj` : 'Mesajlar'}
        aria-label={unread > 0 ? `${unread} okunmamış mesaj` : 'Mesajlar'}
      >
        <ChatIcon />
        {unread > 0 && <span className="tcp-trigger-badge">{badge}</span>}
      </button>

      {/* PORTAL ŞART: .ui-topbar'da `backdrop-filter: blur(14px)` var (Topbar.css). Değeri
          `none` olmayan bir backdrop-filter, alt ağaçtaki position:fixed öğeler için
          CONTAINING BLOCK yaratır — panel viewport'a değil TOPBAR KUTUSUNA sabitlenir ve
          topbar yüksekliğinde küçük bir kutu olarak çizilir. Bu yüzden <aside> DOM'da
          body'ye taşınır; düğme topbar'da kalır. AppInstallItem da aynı sebeple portal
          kullanıyor. Buradan çıkarılırsa panel yeniden küçülür.

          MODAL DEĞİL: scrim yok, dışarı tıklayınca kapanmaz. Kullanıcı panel açıkken
          sayfayla çalışmaya devam edebilmeli — sohbetin varlık sebebi bu. */}
      {createPortal(
        <aside
        className={`tcp-drawer${open ? ' is-open' : ''}${resizing ? ' is-resizing' : ''}`}
        style={{ width }}
        aria-label="Mesajlar"
      >
        {/* Sol kenardan sürükle: genişlik tarayıcıda hatırlanır. */}
        <div
          className="tcp-resizer"
          onPointerDown={onResizeStart}
          onDoubleClick={() => { setWidth(DEFAULT_WIDTH); localStorage.setItem(WIDTH_KEY, String(DEFAULT_WIDTH)); }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Panel genişliğini ayarla (çift tık: varsayılana dön)"
          title="Sürükleyerek genişlet · çift tıkla sıfırla"
        />

        <header className="tcp-head">
          {active && (
            <button type="button" className="tcp-back" onClick={() => setActive(null)} aria-label="Geri">
              ‹
            </button>
          )}
          <strong className="tcp-title">{active ? active.title : 'Mesajlar'}</strong>
          <button type="button" className="tcp-close" onClick={() => setOpen(false)} aria-label="Kapat">×</button>
        </header>

        {error && <div className="tcp-error" onClick={() => setError(null)}>{error}</div>}
        {notice && <div className="tcp-notice" onClick={() => setNotice(null)}>{notice}</div>}

        {!active ? (
          <div className="tcp-list">
            {conversations.length > 0 && <div className="tcp-section">Sohbetler</div>}
            {conversations.map((c) => {
              const id = c.id ?? c.Id;
              const title = c.title ?? c.Title;
              const isGroup = (c.kind ?? c.Kind) === 'TEAM_GROUP';
              const count = c.unreadCount ?? c.UnreadCount ?? 0;
              const preview = c.lastMessagePreview ?? c.LastMessagePreview;
              const mine = c.lastMessageIsMine ?? c.LastMessageIsMine;
              return (
                <button key={id} type="button" className="tcp-row" onClick={() => openExisting(c)}>
                  <span className={`tcp-avatar${isGroup ? ' is-group' : ''}`}>{initials(title)}</span>
                  <span className="tcp-row-main">
                    <span className="tcp-row-title">{title}</span>
                    <span className="tcp-row-sub">
                      {preview ? (mine ? `Siz: ${preview}` : preview) : 'Henüz mesaj yok'}
                    </span>
                  </span>
                  <span className="tcp-row-side">
                    <span className="tcp-row-time">{clock(c.lastMessageDate ?? c.LastMessageDate)}</span>
                    {count > 0 && <span className="tcp-row-badge">{count > 99 ? '99+' : count}</span>}
                  </span>
                </button>
              );
            })}

            {pendingGroups.length > 0 && <div className="tcp-section">Ekip grupları</div>}
            {pendingGroups.map((g) => (
              <button key={g.id} type="button" className="tcp-row" onClick={() => openGroup(g)}>
                <span className="tcp-avatar is-group">{initials(g.name)}</span>
                <span className="tcp-row-main">
                  <span className="tcp-row-title">{g.name}</span>
                  <span className="tcp-row-sub">
                    {(g.members?.length ?? 0)} kişi{g.isLeader ? ' · Ekibiniz' : ''}
                  </span>
                </span>
              </button>
            ))}

            {pendingContacts.length > 0 && <div className="tcp-section">Kişiler</div>}
            {pendingContacts.map((c) => (
              <button key={c.userId} type="button" className="tcp-row" onClick={() => openDirect(c)}>
                <span className="tcp-avatar">{initials(c.fullName)}</span>
                <span className="tcp-row-main">
                  <span className="tcp-row-title">{c.fullName}</span>
                  <span className="tcp-row-sub">{relationLabel(c.relation, c.depth, c.levelName)}</span>
                </span>
              </button>
            ))}

            {groups.length > 0 && (
              // Kardeşlerin neden listede olmadığını burada söylüyoruz: "meslektaşımı neden
              // bulamıyorum" sorusu desteğe gitmeden cevaplansın.
              <p className="tcp-hint">
                Aynı yöneticiye bağlı meslektaşlarınızla <b>ekip grubu</b> üzerinden yazışırsınız;
                birebir sohbet yöneticileriniz ve astlarınız içindir.
              </p>
            )}

            {conversations.length === 0 && pendingGroups.length === 0 && pendingContacts.length === 0 && (
              <p className="tcp-hint">
                Organizasyon şemasında sizinle bağlantılı ve oturum hesabı olan bir kişi bulunmuyor.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="tcp-body" ref={bodyRef}>
              {messages.length === 0 && (
                <p className="tcp-hint">Henüz mesaj yok. İlk mesajı siz yazın.</p>
              )}
              {messages.map((m) => {
                const id = m.id ?? m.Id;
                const isMine = m.isMine ?? m.IsMine;
                const masked = m.isMasked ?? m.IsMasked;
                const read = m.readByCounterpart ?? m.ReadByCounterpart;
                return (
                  <div key={id} className={`tcp-msg${isMine ? ' is-mine' : ''}`}>
                    {/* Grupta kim yazdı şart; birebirde iki kişi var, gereksiz gürültü. */}
                    {active.isGroup && !isMine && (
                      <span className="tcp-msg-sender">{m.senderName ?? m.SenderName}</span>
                    )}
                    <span className="tcp-msg-body">{m.body ?? m.Body}</span>
                    <span className="tcp-msg-meta">
                      {clock(m.createdDate ?? m.CreatedDate)}
                      {isMine && read === true && <span className="tcp-msg-read" title="Okundu"> ✓✓</span>}
                    </span>
                    {/* Maskeleme notu YALNIZ kendi mesajında: karşı taraf için maskeli metin
                        mesajın normal hâlidir, "bu kişi yasaklı kelime kullandı" bilgisi
                        denetim ekranına aittir. */}
                    {isMine && masked && (
                      <span className="tcp-msg-masked">Kullanılamayan kelime maskelendi</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="tcp-compose">
              <textarea
                className="tcp-input"
                rows={3}
                placeholder="Mesaj yazın…  (Enter gönderir, Shift+Enter satır atlar)"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Enter gönderir, Shift+Enter satır atlar — sohbet beklentisi budur.
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
              />
              <button
                type="button"
                className="tcp-send"
                onClick={send}
                disabled={!draft.trim() || sending}
                aria-label="Gönder"
              >
                {sending ? '…' : '➤'}
              </button>
            </div>
          </>
        )}
        </aside>,
        document.body,
      )}
    </>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.3 9.3 0 0 1-3.3-.6L3 21l1.8-5a8.2 8.2 0 0 1-.8-3.5 8.4 8.4 0 0 1 8.5-8.4 8.4 8.4 0 0 1 8.5 8.4Z" />
    </svg>
  );
}

export default TeamChatPanel;
